// فهرستِ دوستان از دو منبع ساخته می‌شه، و این‌جا تصمیم می‌گیره کدوم ردیف
// کجا دیده شه. عمداً خالص و بدونِ ایمپورت، تا با `npm test` تست شه.
//
// دو منبع:
//   friendships  رابطه‌ی دوطرفه‌ی بینِ دو حسابِ اپ (pending یا accepted)
//   contacts     مخاطبِ دوست‌شده‌ی دفترچه‌ی خودم؛ ممکنه اصلاً حساب نداشته باشه
//
// قاعده‌ی این فایل یه جمله‌ست: **یه آدم، یه ردیف، توی یه جا.** تا وقتی طرف
// حساب نداره، ردیفِ دفترچه‌ایش کنارِ بقیه‌ی دوستان می‌شینه (با نشانِ «هنوز
// عضو نیست»). به‌محضِ اینکه حساب داره، **فقط** ردیفِ دوستی نماینده‌شه — یا
// بالای صفحه منتظرِ جوابه، یا پایین توی «درخواست فرستاده شد»، یا به‌عنوانِ
// دوستِ اپ توی فهرست. ردیفِ دوستی نداره؟ پس دوست نیست (WHOSUP-261).

/** هر چیزی که یه مخاطبِ دفترچه‌ایه؛ فقط همین یه فیلد اهمیت داره. */
export interface ContactLike {
  matchedUserId?: string | null;
}

/** هر چیزی که یه ردیفِ دوستیه — چه pending چه accepted. */
export interface FriendLike {
  profile: { id: string };
}

/**
 * مخاطب‌هایی که باید توی فهرستِ اصلی دیده بشن: فقط اون‌هایی که حساب ندارن.
 *
 * ═══ باگِ اول: یه آدم دو بار ═══
 * قبلاً فهرستِ موبایل جمعِ سرراستِ «دوستانِ اپ + مخاطبینِ دوست‌شده» بود. از
 * وقتی دعوت خودکار مخاطب می‌سازه، لحظه‌ی عضو شدنِ طرف همون آدم **دو بار**
 * دیده می‌شد: یه بار به‌عنوانِ مخاطب، یه بار به‌عنوانِ درخواستِ فرستاده‌شده.
 *
 * ═══ باگِ دوم: «دوسته» بی‌آنکه طرف قبول کرده باشه (WHOSUP-261) ═══
 * بعد از اولی، مخاطبِ مچ‌شده‌ای که **هیچ** ردیفِ دوستیِ زنده‌ای نداشت هنوز
 * می‌موند — به این خیال که «جای دیگه‌ای برای نشون دادنش نیست». ولی این
 * حالت پیش می‌آد دقیقاً وقتی درخواست **ساخته نشده**: دوستیِ قبلی حذف یا رد
 * شده بود (تریگرِ 0024 ردیفِ قدیمی رو زنده نمی‌کنه)، یا طرف بلاک بود (تریگرِ
 * 0146 درج رو بی‌صدا می‌اندازه). نتیجه: ردیفی با نشانِ «اعلانِ اپ» توی
 * فهرستِ دوستان، بی‌هیچ «در انتظار» — یعنی دوستیِ یک‌طرفه‌ای که طرف هرگز
 * قبولش نکرده، و حتی بعد از بلاک همون‌جا می‌موند.
 *
 * حالا عضوِ اپ فقط از راهِ ردیفِ دوستی توی فهرست می‌آد. جای کسی که ردیفِ
 * دوستی نداره فهرستِ «از مخاطبینت، عضوِ اپ» پایینِ صفحه‌ی موبایله — با
 * دکمه‌ی درخواست — و اگه بلاک باشه، هیچ‌جا (نگاه کن به `withoutBlocked`).
 */
export function contactFriendsToShow<T extends ContactLike>(contacts: readonly T[]): T[] {
  return contacts.filter(isOffApp);
}

/**
 * «این آدم هنوز عضو نیست.»
 *
 * تنها معیارش وجودِ یه حسابِ مچ‌شده‌ست، نه داشتنِ شماره یا ایمیل: راهِ
 * رسیدن (`reachOf`) سوالِ دیگه‌ایه — این یکی می‌گه طرف اپ رو داره یا نه.
 */
export function isOffApp(contact: ContactLike): boolean {
  return !contact.matchedUserId;
}

/**
 * هر ردیفی که پشتش کسیه که من بلاکش کرده‌م، بیرون (WHOSUP-261).
 *
 * خودِ دیتابیس دوستی و درخواست و کشفِ مخاطب رو موقعِ بلاک قطع می‌کنه
 * (مایگریشن 0146)، ولی **دفترچه‌ی خودِ من** مالِ منه و دست نمی‌خوره: ردیفِ
 * مخاطب هنوز `matchedUserId`ِ اون آدم رو داره، و تطبیقِ محلیِ گوشی هم
 * ممکنه از قبل از بلاک مونده باشه. بدونِ این فیلتر، همون آدم پایینِ صفحه
 * به‌عنوانِ «عضوِ اپ، درخواست بده» و توی انتخابگرِ مخاطبین دوباره پیدا می‌شد.
 * تا رفعِ مسدودیت، هیچ‌جا.
 *
 * فقط جهتِ «من بلاک کرده‌م» رو می‌شه دونست — سیاستِ `b_all` بیشتر از این
 * نمی‌ده، و عمداً: جهتِ دیگه رو خودِ دیتابیس بی‌صدا می‌بنده.
 */
export function withoutBlocked<T>(
  rows: readonly T[],
  blockedIds: Iterable<string>,
  userIdOf: (row: T) => string | null | undefined,
): T[] {
  const blocked = new Set(blockedIds);
  if (blocked.size === 0) return [...rows];
  return rows.filter((r) => {
    const id = userIdOf(r);
    return !id || !blocked.has(id);
  });
}

/** مخاطبی که راه‌های رسیدنش هم معلومه — برای تصمیمِ «از این‌جا می‌شه دعوتش کرد؟». */
export interface ReachLike extends ContactLike {
  phones: readonly string[];
  emails: readonly string[];
}

/**
 * دوستِ دفترچه‌ای که **تنها** راهِ رسیدن بهش شماره تلفنه.
 *
 * وب پیامک نمی‌فرسته، پس این آدم‌ها از انتخابگرِ دعوتِ وب انتخاب‌شدنی
 * نیستن — ولی غیب شدنشون بدتره: کاربر فکر می‌کنه دوستش گم شده. پس دیده
 * می‌شن، خاکستری، با توضیحِ «این‌جا نمی‌شه» (WHOSUP-143).
 *
 * کسی که ایمیل هم داره این‌جا نیست — راهِ ایمیلیِ وب بهش می‌رسه — و کسی که
 * حساب داره هم نه: اون رو ردیفِ دوستی نمایندگی می‌کنه (همون قاعده‌ی
 * `contactFriendsToShow`).
 */
export function phoneOnlyContacts<T extends ReachLike>(contacts: readonly T[]): T[] {
  return contactFriendsToShow(contacts)
    .filter((c) => c.phones.length > 0 && c.emails.length === 0);
}
